"use client";

import { useEffect, useRef } from "react";
import { useStore } from "zustand";
import type { StoreApi } from "zustand/vanilla";

const scopedStores = new Map<string, StoreApi<unknown>>();
const scopedStoreRefCounts = new Map<string, number>();

export function resetScopedStoresForTests() {
  if (process.env.NODE_ENV !== "test") return;
  scopedStores.clear();
  scopedStoreRefCounts.clear();
}

export function useScopedStore<TState, TSelected>(
  scopeKey: string,
  createStore: () => StoreApi<TState>,
  selector: (state: TState) => TSelected
) {
  const scopedRef = useRef<{ key: string; store: StoreApi<TState> } | null>(null);

  if (!scopedRef.current || scopedRef.current.key !== scopeKey) {
    const existing = scopedStores.get(scopeKey) as StoreApi<TState> | undefined;
    const store = existing ?? createStore();
    if (!existing) {
      scopedStores.set(scopeKey, store as StoreApi<unknown>);
    }
    scopedRef.current = {
      key: scopeKey,
      store
    };
    // Track ref count for cleanup
    const current = scopedStoreRefCounts.get(scopeKey) ?? 0;
    scopedStoreRefCounts.set(scopeKey, current + 1);
  }

  // Cleanup on unmount - decrement ref count and delete store when no refs remain
  useEffect(() => {
    const currentKey = scopeKey;
    return () => {
      const count = scopedStoreRefCounts.get(currentKey) ?? 0;
      if (count <= 1) {
        scopedStoreRefCounts.delete(currentKey);
        scopedStores.delete(currentKey);
      } else {
        scopedStoreRefCounts.set(currentKey, count - 1);
      }
    };
  }, [scopeKey]);

  return useStore(scopedRef.current.store, selector);
}